<template>
  <div class="wrapper">
    <CCard>
      <CCardHeader>
        <strong>People Without iCare</strong>
      </CCardHeader>
      <CCardBody>
        <form class="filter-panel mb-3" @submit.prevent="applyFilters">
          <CRow class="align-items-start">
            <CCol md="12" class="mb-3">
              <div class="tab-row">
                <CButton
                  type="button"
                  color="secondary"
                  class="mr-2 mb-2"
                  :variant="type === 'never_joined' ? '' : 'outline'"
                  @click="switchType('never_joined')"
                >
                  Never Joined (Newcomer)
                </CButton>
                <CButton
                  type="button"
                  color="secondary"
                  class="mr-2 mb-2"
                  :variant="type === 'previously_had' ? '' : 'outline'"
                  @click="switchType('previously_had')"
                >
                  Previously Had iCare
                </CButton>
                <CButton
                  type="button"
                  color="secondary"
                  class="mr-2 mb-2"
                  :variant="type === 'close_by_icare' ? '' : 'outline'"
                  @click="switchType('close_by_icare')"
                >
                  Close By iCare
                </CButton>
              </div>
            </CCol>
          </CRow>

          <CRow class="align-items-end">
            <CCol :md="showFollowupFilters ? 4 : 6" class="mb-3">
              <label class="filter-label">{{ searchLabel }}</label>
              <CInput
                v-model.trim="search"
                :placeholder="searchPlaceholder"
                @keyup.enter="applyFilters"
              />
            </CCol>

            <CCol v-if="type === 'previously_had'" md="4" class="mb-3">
              <label class="filter-label">Follow Up Status</label>
              <CSelect
                :value.sync="followup_status"
                :options="followupStatusFilterOptions"
                @keyup.enter.native="applyFilters"
              />
            </CCol>
          </CRow>

          <CRow>
            <CCol md="12" class="text-right">
              <CButton color="info" class="mr-2" type="submit">Search</CButton>
              <CButton color="secondary" variant="outline" @click.prevent="resetFilters">Reset</CButton>
            </CCol>
          </CRow>
        </form>

        <div class="table-toolbar d-flex justify-content-between align-items-center mb-2 flex-wrap">
          <div class="d-flex align-items-center mb-2 mb-md-0">
            <label class="mr-2 mb-0">Rows</label>
            <CSelect
              class="per-page-select"
              :value.sync="per_page_option"
              :options="perPageOptions"
              @change="handlePerPageChange"
            />
          </div>
          <div class="text-muted mb-2 mb-md-0">
            Showing {{ meta.from || 0 }} - {{ meta.to || 0 }} of {{ meta.total || 0 }}
          </div>
        </div>

        <div class="table-responsive">
          <table class="table table-striped table-hover align-middle">
            <thead>
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th>Email</th>
                <th>{{ historyColumnTitle }}</th>
                <th v-if="showFollowupTable">Follow Up</th>
                <th v-if="showFollowupTable" style="width: 160px">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading">
                <td :colspan="showFollowupTable ? 6 : 4">Loading...</td>
              </tr>
              <tr v-for="item in items" :key="item.people_uuid">
                <td>
                  <div class="font-weight-semibold">{{ item.fullname }}</div>
                </td>
                <td>
                  <CBadge :color="statusBadgeColor(item.status)">{{ item.status || '-' }}</CBadge>
                </td>
                <td>{{ item.email || '-' }}</td>
                <td>
                  <span v-if="type === 'never_joined'">
                    {{ formatDate(item.join_date) }}
                  </span>
                  <span v-else-if="type === 'close_by_icare'">
                    {{ item.followup_icare_name || '-' }}
                  </span>
                  <span v-else-if="item.last_icare_history">
                    {{ item.last_icare_history.old_icare_name || item.last_icare_history.old_value || '-' }}
                    -> {{ item.last_icare_history.new_icare_name || item.last_icare_history.new_value || '-' }}
                    <br>
                    <small class="text-muted">{{ formatDateTime(item.last_icare_history.logged_at) }}</small>
                  </span>
                  <span v-else>-</span>
                </td>
                <td v-if="showFollowupTable">
                  <div class="followup-summary">
                    <div class="mb-1">
                      <CBadge :color="followupBadgeColor(item)">{{ followupStatusLabel(item) }}</CBadge>
                    </div>
                    <div class="followup-value text-muted">
                      {{ followupDisplayValue(item) }}
                    </div>
                  </div>
                </td>
                <td v-if="showFollowupTable">
                  <CButton size="sm" color="info" class="mr-1 mb-1" @click="openFollowupModal(item)">
                    {{ type === 'close_by_icare' ? 'View Follow Up' : followupActionLabel(item) }}
                  </CButton>
                </td>
              </tr>
              <tr v-if="!loading && items.length === 0">
                <td :colspan="showFollowupTable ? 6 : 4">No data found.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="d-flex justify-content-between align-items-center mt-3 flex-wrap">
          <div class="mb-2 mb-md-0">
            Showing {{ meta.from || 0 }} - {{ meta.to || 0 }} of {{ meta.total || 0 }}
          </div>
          <div class="page-controls d-flex align-items-center flex-wrap">
            <CButton size="sm" class="mr-1 mb-1" :disabled="(meta.current_page || 1) <= 1" @click="gotoPage((meta.current_page || 1) - 1)">Prev</CButton>
            <CButton
              v-for="pageNumber in visiblePages"
              :key="'p-' + pageNumber"
              size="sm"
              class="mr-1 mb-1"
              :color="pageNumber === (meta.current_page || 1) ? 'primary' : 'light'"
              @click="gotoPage(pageNumber)"
            >
              {{ pageNumber }}
            </CButton>
            <CButton size="sm" class="mb-1" :disabled="(meta.current_page || 1) >= (meta.last_page || 1)" @click="gotoPage((meta.current_page || 1) + 1)">Next</CButton>
          </div>
        </div>
      </CCardBody>
    </CCard>

    <CModal
      color="info"
      :closeOnBackdrop="false"
      :show.sync="showFollowupModal"
      title="Follow Up"
      size="lg"
    >
      <div v-if="followupForm.people_uuid">
        <div class="mb-3">
          <div class="text-muted small">People</div>
          <div class="font-weight-bold">{{ followupForm.fullname }}</div>
          <div class="text-muted small">{{ followupForm.email || '-' }}</div>
        </div>

        <div v-if="!isCloseByIcareViewMode" class="modal-section mb-3">
          <div class="text-muted small mb-1">Current Follow Up</div>
          <div class="d-flex flex-wrap align-items-center">
            <CBadge :color="followupBadgeColor(followupForm.current_followup)">{{ followupStatusLabel(followupForm.current_followup) }}</CBadge>
            <span class="ml-2 text-muted">{{ followupDisplayValue(followupForm.current_followup) }}</span>
          </div>
        </div>

        <div v-if="!isCloseByIcareViewMode" class="modal-section mb-3">
          <div class="text-muted small mb-2">Status</div>
          <CSelect :value.sync="followupForm.status" :options="followupStatusOptions" />
          <small class="text-muted d-block mt-2">
            Use <b>In Progress</b> while the follow up is still ongoing. It needs a reason so the trail stays clear.
          </small>
        </div>

        <div v-if="!isCloseByIcareViewMode && followupForm.status === 'in_progress'" class="modal-section mb-3">
          <label class="filter-label">Reason</label>
          <CTextarea
            v-model.trim="followupForm.reason"
            rows="4"
            placeholder="Write the reason for this follow up"
          />
        </div>

        <div v-if="!isCloseByIcareViewMode && followupForm.status === 'close'" class="modal-section mb-3">
          <div class="text-muted small mb-2">Close Method</div>
          <div class="d-flex flex-wrap">
            <label class="radio-chip mr-3 mb-2">
              <input type="radio" value="reason" v-model="followupForm.close_type">
              <span>Reason</span>
            </label>
            <label class="radio-chip mb-2">
              <input type="radio" value="assign_icare" v-model="followupForm.close_type">
              <span>Assign iCare</span>
            </label>
          </div>
          <div v-if="followupForm.close_type === 'reason'" class="mt-3">
            <label class="filter-label">Reason</label>
            <CTextarea
              v-model.trim="followupForm.reason"
              rows="4"
              placeholder="Write the reason for closing follow up"
            />
          </div>

          <div v-else class="mt-3">
            <label class="filter-label">Select iCare</label>
            <CSelect :value.sync="followupForm.icare_id" :options="icareOptions" />
            <small class="text-muted d-block mt-2">
              This will assign the person to the selected iCare and close the follow up automatically.
            </small>
          </div>
        </div>

        <div class="modal-section mb-3">
          <div class="text-muted small mb-2">Log Journey</div>
          <div v-if="followupHistoryEntries().length > 0" class="followup-log-scroll">
            <div class="followup-log-list">
              <div
                v-for="entry in followupHistoryEntries()"
                :key="entry.id"
                class="followup-log-item"
              >
                <div class="d-flex justify-content-between align-items-start flex-wrap">
                  <div class="mb-1">
                    <CBadge :color="followupHistoryBadgeColor(entry)">{{ followupHistoryLabel(entry) }}</CBadge>
                  </div>
                  <div class="text-right">
                    <small class="text-muted d-block">{{ followupHistoryTime(entry) }}</small>
                    <small class="text-muted d-block">{{ followupHistoryCheckedBy(entry) }}</small>
                  </div>
                </div>
                <div class="followup-log-value text-muted">
                  {{ followupHistoryValue(entry) }}
                </div>
              </div>
            </div>
          </div>
          <div v-else class="text-muted small">No log yet.</div>
        </div>
      </div>

      <template #footer>
        <CButton color="secondary" variant="outline" @click="closeFollowupModal">
          {{ isCloseByIcareViewMode ? 'Close' : 'Cancel' }}
        </CButton>
        <CButton v-if="!isCloseByIcareViewMode" color="info" :disabled="followupSubmitting" @click="submitFollowupClose">
          {{ followupSubmitting ? 'Saving...' : 'Save' }}
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import icarePeopleDetail from '@/services/icarePeopleDetail'

export default {
  name: 'IcarePeopleWithoutIcare',
  data() {
    return {
      loading: false,
      items: [],
      search: '',
      type: 'never_joined',
      page: 1,
      per_page: 10,
      per_page_option: '10',
      followup_status: 'all',
      followupSubmitting: false,
      showFollowupModal: false,
      followupForm: {
        people_uuid: '',
        fullname: '',
        email: '',
        status: 'open',
        close_type: 'reason',
        reason: '',
        icare_id: '',
        current_followup: null,
        journey_logs: [],
        view_only: false
      },
      followupStatusOptions: [
        { value: 'open', label: 'Open' },
        { value: 'in_progress', label: 'In Progress' },
        { value: 'close', label: 'Close' }
      ],
      followupStatusFilterOptions: [
        { value: 'all', label: 'All' },
        { value: 'open', label: 'Open' },
        { value: 'in_progress', label: 'In Progress' },
        { value: 'close', label: 'Close' }
      ],
      icareOptions: [{ value: '', label: 'Select iCare' }],
      meta: {},
      perPageOptions: [
        { value: '10', label: '10' },
        { value: '50', label: '50' },
        { value: '100', label: '100' },
        { value: 'all', label: 'All' }
      ]
    }
  },
  computed: {
    showFollowupFilters() {
      return this.type === 'previously_had'
    },
    showFollowupTable() {
      return ['previously_had', 'close_by_icare'].includes(this.type)
    },
    isCloseByIcareViewMode() {
      return this.type === 'close_by_icare'
    },
    searchLabel() {
      return this.showFollowupTable
        ? 'Search Name / Email / Last iCare'
        : 'Search People Name'
    },
    searchPlaceholder() {
      return this.showFollowupTable
        ? 'Type name, email, or last iCare'
        : 'Type people name'
    },
    historyColumnTitle() {
      if (this.type === 'close_by_icare') return 'iCare'
      return this.type === 'never_joined' ? 'Join Date' : 'Last iCare History'
    },
    visiblePages() {
      const current = this.meta.current_page || 1
      const last = this.meta.last_page || 1
      const start = Math.max(1, current - 2)
      const end = Math.min(last, current + 2)
      const pages = []
      for (let p = start; p <= end; p++) pages.push(p)
      return pages
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    applyFilters() {
      this.page = 1
      this.loadData()
    },
    resetFilters() {
      this.search = ''
      this.followup_status = 'all'
      this.per_page_option = '10'
      this.page = 1
      this.loadData()
    },
    switchType(type) {
      this.type = type
      this.page = 1
      this.followup_status = 'all'
      this.search = ''
      this.loadData()
    },
    loadData() {
      this.loading = true
      const requestedPerPage = this.per_page_option === 'all'
        ? Math.max(this.meta.total || 0, 1000)
        : Number(this.per_page_option || 10)

      this.per_page = requestedPerPage

      icarePeopleDetail
        .getPeopleWithoutIcare({
          page: this.page,
          per_page: this.per_page,
          search: this.search,
          type: this.type,
          followup_status: this.type === 'previously_had' ? this.followup_status : undefined
        })
        .then((res) => {
          const payload = res.data || {}
          const metaInfo = res.meta || {}
          this.items = payload.data || []
          this.meta = {
            current_page: payload.current_page || 1,
            last_page: payload.last_page || 1,
            total: payload.total || 0,
            from: payload.from || 0,
            to: payload.to || 0
          }
          this.syncMetaOptions(metaInfo)
        })
        .catch((err) => {
          this.items = []
          alert((err.response && err.response.data && err.response.data.message) || 'Failed loading data')
        })
        .finally(() => {
          this.loading = false
        })
    },
    syncMetaOptions(metaInfo) {
      if (Array.isArray(metaInfo.followup_status_options) && metaInfo.followup_status_options.length > 0) {
        this.followupStatusFilterOptions = metaInfo.followup_status_options
      }
      if (Array.isArray(metaInfo.available_icares) && metaInfo.available_icares.length > 0) {
        this.icareOptions = [
          { value: '', label: 'Select iCare' },
          ...metaInfo.available_icares.map((item) => ({
            value: String(item.icare_id),
            label: item.icare_name
          }))
        ]
      }
    },
    gotoPage(pageNumber) {
      const target = Number(pageNumber)
      if (!Number.isInteger(target)) return
      if (target < 1 || target > (this.meta.last_page || 1)) return
      this.page = target
      this.loadData()
    },
    handlePerPageChange() {
      this.page = 1
      this.loadData()
    },
    openFollowupModal(item) {
      const followup = item && item.followup ? item.followup : null
      const isAssignIcare = followup && followup.close_type === 'assign_icare'
      const currentStatus = followup && followup.status ? followup.status : 'open'
      this.followupForm = {
        people_uuid: item.people_uuid,
        fullname: item.fullname,
        email: item.email,
        status: currentStatus === 'closed' || currentStatus === 'resolved' ? 'close' : currentStatus,
        close_type: isAssignIcare ? 'assign_icare' : 'reason',
        reason: '',
        icare_id: followup && followup.close_type === 'assign_icare' && followup.close_context_id
          ? String(followup.close_context_id)
          : '',
        current_followup: followup,
        journey_logs: Array.isArray(item.journey_logs) ? item.journey_logs : [],
        view_only: this.type === 'close_by_icare'
      }
      this.showFollowupModal = true
    },
    closeFollowupModal() {
      this.showFollowupModal = false
      this.followupSubmitting = false
      this.followupForm.reason = ''
    },
    submitFollowupClose() {
      const payload = {
        people_uuid: this.followupForm.people_uuid,
        status: this.followupForm.status
      }

      if (this.followupForm.status === 'close') {
        payload.is_checked = true
        payload.close_type = this.followupForm.close_type

        if (this.followupForm.close_type === 'reason') {
          const reason = String(this.followupForm.reason || '').trim()
          if (!reason) {
            alert('Reason is required')
            return
          }
          payload.reason = reason
        } else {
          if (!this.followupForm.icare_id) {
            alert('Please select an iCare')
            return
          }
          payload.icare_id = Number(this.followupForm.icare_id)
        }
      } else if (this.followupForm.status === 'in_progress') {
        const reason = String(this.followupForm.reason || '').trim()
        if (!reason) {
          alert('Reason is required')
          return
        }
        payload.is_checked = true
        payload.reason = reason
      } else {
        payload.is_checked = false
      }

      this.followupSubmitting = true
      icarePeopleDetail
        .savePeopleWithoutIcareFollowup(payload)
        .then(() => {
          this.followupForm.reason = ''
          this.showFollowupModal = false
          this.loadData()
        })
        .catch((err) => {
          alert((err.response && err.response.data && err.response.data.message) || 'Failed to save follow-up')
        })
        .finally(() => {
          this.followupSubmitting = false
        })
    },
    followupStatusLabel(item) {
      const followup = item && item.followup ? item.followup : item
      if (!followup) return 'Open'
      if (followup.status === 'close') {
        if (followup.close_type === 'assign_icare') return 'Closed by iCare'
        if (followup.close_type === 'reason') return 'Closed by Reason'
        return 'Closed'
      }
      if (followup.status === 'in_progress') return 'In Progress'
      if (followup.status === 'open') return 'Open'
      if (followup.is_checked) return 'Open'
      return 'Open'
    },
    followupBadgeColor(item) {
      const followup = item && item.followup ? item.followup : item
      if (!followup) return 'secondary'
      if (followup.status === 'close') {
        return followup.close_type === 'assign_icare' ? 'success' : 'warning'
      }
      if (followup.status === 'in_progress') return 'info'
      if (followup.is_checked) return 'info'
      return 'secondary'
    },
    followupDisplayValue(item) {
      const followup = item && item.followup ? item.followup : item
      if (!followup) return '-'
      return followup.display_value || '-'
    },
    followupHistoryEntries() {
      return Array.isArray(this.followupForm.journey_logs) ? this.followupForm.journey_logs : []
    },
    followupHistoryLabel(entry) {
      if (entry && entry.source === 'people_log') return entry.label || 'iCare Log'
      const status = String((entry && entry.value_json && entry.value_json.status) || '').toLowerCase()
      if (status === 'in_progress') return 'In Progress'
      if (status === 'close') {
        const closeType = String((entry && entry.value_json && entry.value_json.close_type) || '').toLowerCase()
        if (closeType === 'assign_icare') return 'Close - Assign iCare'
        if (closeType === 'reason') return 'Close - Reason'
        return 'Close'
      }
      if (status === 'open') return 'Open'
      return 'Follow Up'
    },
    followupHistoryBadgeColor(entry) {
      if (entry && entry.source === 'people_log') {
        if (entry.action === 'promote') return 'info'
        if (entry.action === 'demote') return 'secondary'
        if (entry.action === 'move') return 'primary'
        return 'dark'
      }
      const status = String((entry && entry.value_json && entry.value_json.status) || '').toLowerCase()
      if (status === 'close') {
        const closeType = String((entry && entry.value_json && entry.value_json.close_type) || '').toLowerCase()
        return closeType === 'reason' ? 'warning' : 'success'
      }
      if (status === 'in_progress') return 'info'
      return 'secondary'
    },
    followupHistoryValue(entry) {
      if (!entry) return '-'
      const valueJson = entry.value_json || {}
      if (valueJson.reason) return valueJson.reason
      if (valueJson.close_value) return valueJson.close_value
      if (entry.value_text) return entry.value_text
      return '-'
    },
    followupHistoryTime(entry) {
      return this.formatLogDateTime((entry && (entry.checked_at || entry.created_at)) || null)
    },
    followupHistoryCheckedBy(entry) {
      if (!entry) return '-'
      return entry.checked_by_name ? `By ${entry.checked_by_name}` : 'By -'
    },
    followupActionLabel(item) {
      if (this.type === 'close_by_icare') {
        return 'View Follow Up'
      }
      const followup = item && item.followup ? item.followup : null
      if (!followup || !followup.status || followup.status === 'open') {
        return 'Start Follow Up'
      }
      if (followup.status === 'in_progress') {
        return 'Edit Follow Up'
      }
      if (followup.status === 'close' || followup.status === 'closed' || followup.status === 'resolved') {
        return 'View Follow Up'
      }
      return 'Edit Follow Up'
    },
    statusBadgeColor(status) {
      const normalized = String(status || '').toLowerCase()
      if (normalized === 'member') return 'success'
      if (normalized === 'visitor') return 'warning'
      if (normalized === 'newcomer') return 'info'
      return 'secondary'
    },
    formatDate(value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return value
      return date.toLocaleDateString()
    },
    formatDateTime(value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return value
      return date.toLocaleString()
    },
    formatLogDateTime(value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return value
      const pad = (num) => String(num).padStart(2, '0')
      return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`
    }
  }
}
</script>

<style scoped>
.per-page-select {
  width: 130px;
}
.filter-panel {
  border: 1px solid #d8dee8;
  border-radius: 10px;
  padding: 14px;
  background: #f8fafd;
}
.filter-label {
  display: block;
  font-size: 12px;
  font-weight: 700;
  color: #4a5568;
  margin-bottom: 6px;
}
.tab-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
}
.page-controls {
  display: flex;
  align-items: center;
}
.followup-summary {
  min-width: 220px;
}
.followup-value {
  font-size: 12px;
  line-height: 1.35;
  word-break: break-word;
}
.modal-section {
  padding: 12px 14px;
  border: 1px solid #e4e9f2;
  border-radius: 10px;
  background: #fbfcfe;
}
.followup-log-list {
  display: grid;
  gap: 10px;
}
.followup-log-scroll {
  max-height: 320px;
  overflow-y: auto;
  padding-right: 4px;
}
.followup-log-item {
  padding: 10px 12px;
  border: 1px solid #e4e9f2;
  border-radius: 8px;
  background: #fff;
}
.followup-log-value {
  font-size: 12px;
  line-height: 1.45;
  margin-top: 4px;
  word-break: break-word;
}
.followup-log-meta {
  font-size: 12px;
  line-height: 1.35;
  margin-top: 2px;
}
.radio-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid #cfd6e4;
  border-radius: 999px;
  background: #fff;
  cursor: pointer;
  user-select: none;
}
.radio-chip input {
  margin: 0;
}
.radio-chip span {
  font-weight: 600;
  font-size: 13px;
}
.align-middle td {
  vertical-align: middle;
}
</style>
